/* ---------------------------------------------------------------------------
   The register rolled up for a network-level report.

   Everything else in this application answers a question about one structure.
   This answers questions about a division: how many bridges, in what condition,
   how many inspections are outstanding, and how much of that is actually known.

   THREE THINGS THIS FILE IS CAREFUL ABOUT.

   **Condition is compared across sheets that may not be comparable.** DoR
   inventory sheets record one overall rating per visit on whatever scale that
   sheet used — the one real sheet in this project's folder carries `2012: 1
   (0-4 scale)` and `2016: 7 (0-10 scale)`. To count a network-wide distribution
   at all, a rating has to be read as a FRACTION of its own scale's top, and that
   is an assumption: it presumes a 3 out of 4 and a 7 out of 10 mean roughly the
   same thing about a bridge, which two rubrics written years apart need not.
   The assumption is made because the alternative is no network condition figure
   at all, and it is declared — `conditionSummary` reports the mix of scales it
   worked from so a reader can see whether the comparison is fair.

   **Higher is better.** Zero is a real and bad score on every scale the sheet
   uses, which is why an element nobody assessed carries no entry rather than a
   nought. The bands below run the same way.

   **A draft is not the register's claim.** The distribution reads the latest
   inspection that has been accepted, or that predates the review workflow and is
   therefore on file as a historical fact. An entry still in `draft` or `review`
   is counted separately and reported as such: a network condition report built
   from unreviewed drafts would be a report of what one person thought last week.
   --------------------------------------------------------------------------- */

import type { Bridge, Inspection } from './types.ts';
import { inspectionStanding, isOutstanding, scaleMax } from './derive.ts';
import { bandCounts, type ScreeningBand } from './screening.ts';
import { consequenceCoverage, type ConsequenceCoverage } from './consequence.ts';
import { sumRecorded } from './recorded.ts';
import { active } from '../tenant/registry.ts';

/* --------------------------- condition --------------------------- */

/**
 * Where a structure sits, from its latest inspection the register stands behind.
 *
 * The last two are absences rather than conditions and are deliberately in the
 * same list: a report that showed four condition bands and quietly dropped the
 * structures it could not place would overstate how much of the network has
 * been assessed.
 */
export type ConditionBand =
  | 'Good'
  | 'Fair'
  | 'Poor'
  | 'Critical'
  | 'Scale not readable'
  | 'Never inspected';

export const CONDITION_BANDS: readonly ConditionBand[] = [
  'Good',
  'Fair',
  'Poor',
  'Critical',
  'Scale not readable',
  'Never inspected',
];

/**
 * Fractions of a scale's top at which each band starts.
 *
 * **Not from a DoR document.** Even quarters of the range, which is the least
 * opinionated split available and is stated as a choice rather than implied by
 * the code. The *Guideline for Inspection and Maintenance of Bridge* would
 * settle it; it is not in this project's folder, and if it names bands then it
 * wins and these should be replaced wholesale — the same instruction the element
 * list in src/data/elements.ts carries, for the same reason.
 */
export const CONDITION_THRESHOLDS: readonly { band: ConditionBand; from: number }[] = [
  { band: 'Good', from: 0.75 },
  { band: 'Fair', from: 0.5 },
  { band: 'Poor', from: 0.25 },
  { band: 'Critical', from: 0 },
];

/**
 * Whether an entry is something the register asserts, rather than something
 * somebody has typed and not yet had checked.
 *
 * Absent state means the entry predates the review workflow — a fact transcribed
 * from a paper sheet, which the register has always treated as true. `draft` and
 * `review` are not.
 */
export function isStated(insp: Inspection): boolean {
  return insp.state == null || insp.state === 'accepted';
}

/** The latest inspection the register stands behind, or null. */
export function latestStated(b: Bridge): Inspection | null {
  return b.inspections.reduce<Inspection | null>(
    (best, i) => (isStated(i) && (best === null || i.date > best.date) ? i : best),
    null,
  );
}

/** Which band one structure falls in, and why. */
export function conditionBand(b: Bridge): ConditionBand {
  const insp = latestStated(b);
  if (insp === null) return 'Never inspected';

  const max = scaleMax(insp.scale);
  /* A scale string this build cannot parse is its own band rather than a guess.
     Dividing by a denominator nobody knows would put a structure somewhere on a
     distribution on the strength of a typo. */
  if (max === null) return 'Scale not readable';

  const fraction = insp.rating / max;
  for (const step of CONDITION_THRESHOLDS) {
    if (fraction >= step.from) return step.band;
  }
  return 'Critical';
}

export interface ConditionSummary {
  counts: Record<ConditionBand, number>;
  /** How many structures each scale string accounted for, worst mix first. */
  scales: { scale: string; count: number }[];
  /**
   * Structures whose most recent entry is a draft or in review.
   *
   * Counted apart from the bands because it is a different fact: somebody has
   * been to these and the register does not yet stand behind what they wrote.
   */
  awaitingReview: number;
}

export function conditionSummary(list: readonly Bridge[]): ConditionSummary {
  const counts: Record<ConditionBand, number> = {
    Good: 0,
    Fair: 0,
    Poor: 0,
    Critical: 0,
    'Scale not readable': 0,
    'Never inspected': 0,
  };
  const scales = new Map<string, number>();
  let awaitingReview = 0;

  for (const b of list) {
    counts[conditionBand(b)]++;

    const stated = latestStated(b);
    if (stated) scales.set(stated.scale, (scales.get(stated.scale) ?? 0) + 1);

    const newest = b.inspections.reduce<Inspection | null>(
      (best, i) => (best === null || i.date > best.date ? i : best),
      null,
    );
    if (newest && !isStated(newest)) awaitingReview++;
  }

  return {
    counts,
    scales: Array.from(scales, ([scale, count]) => ({ count, scale })).sort(
      (a, b) => b.count - a.count || a.scale.localeCompare(b.scale),
    ),
    awaitingReview,
  };
}

/* --------------------------- compliance --------------------------- */

export interface ComplianceSummary {
  total: number;
  /** Structures with at least one cycle outstanding — one structure, one visit. */
  outstanding: number;
  routineOut: number;
  principalOut: number;
  neverInspected: number;
  /** Share of the group with both cycles in hand, 0–1. */
  compliance: number;
}

/**
 * How much of a group is up to date on its inspections.
 *
 * `outstanding` counts STRUCTURES, not cycles, so the two per-cycle figures can
 * legitimately sum to more than it: a bridge whose routine visit and principal
 * inspection are both out is one bridge to send somebody to. The same rule the
 * overview's headline already follows.
 */
export function complianceSummary(list: readonly Bridge[], now: Date = new Date()): ComplianceSummary {
  const standings = list.map((b) => inspectionStanding(b, now));
  const outstanding = standings.filter((s) => isOutstanding(s.worst)).length;

  return {
    total: list.length,
    outstanding,
    routineOut: standings.filter((s) => isOutstanding(s.routine)).length,
    principalOut: standings.filter((s) => isOutstanding(s.principal)).length,
    neverInspected: standings.filter((s) => s.latest === null && s.worst.status !== 'Not in service').length,
    compliance: list.length === 0 ? 0 : (list.length - outstanding) / list.length,
  };
}

/* --------------------------- grouping --------------------------- */

/**
 * What a summary is broken down by — the KEY of one of the tenant's groupings.
 *
 * A string rather than a union since tenancy, and the change is not cosmetic.
 * DoR is organised by district, division road office and road classification;
 * DPWH is organised by contract package, highway section and District
 * Engineering Office, and has no road codes to classify at all. A union here
 * would have been a compile-time claim that every department divides its network
 * the way Nepal does, and the summary screen would have offered a DPWH user
 * three tabs, two of which were always empty.
 *
 * Each tenant declares its own list — `groupings` in src/tenant/types.ts — and
 * the key is what appears in the URL, so it has to outlive a label change.
 */
export type GroupBy = string;

/** The groupings this tenant offers, in order. The first is the default. */
export function groupBys(): readonly GroupBy[] {
  return active().groupings.map((g) => g.key);
}

/** The default breakdown for this tenant: whichever grouping it lists first. */
export function defaultGroupBy(): GroupBy {
  return active().groupings[0]?.key ?? 'district';
}

/** True where this tenant offers that breakdown. Guards a URL and a stored pref. */
export function isGroupBy(value: unknown): value is GroupBy {
  return typeof value === 'string' && active().groupings.some((g) => g.key === value);
}

/**
 * The heading for one breakdown, in the tenant's own words.
 *
 * Falls back to the key rather than throwing, for the same reason `t()` falls
 * back to its English: a stored preference naming a grouping this tenant does
 * not have is a stale preference, and a stale preference should show a slightly
 * wrong heading rather than take the screen down.
 */
export function groupLabel(by: GroupBy): string {
  return active().groupings.find((g) => g.key === by)?.label ?? by;
}

/**
 * The label a structure falls under, or null where the register does not say.
 *
 * Null is returned rather than a string so the caller decides what an unknown
 * group is called — and it is deliberately NOT folded into a "Other" bucket
 * here, because a division summary in which every structure is unattributed is
 * a finding about the register rather than a row in a table.
 */
export function groupKeyOf(b: Bridge, by: GroupBy): string | null {
  return active().groupings.find((g) => g.key === by)?.of(b) ?? null;
}

export interface GroupSummary {
  /** The group's name, or null for the structures the register cannot place. */
  key: string | null;
  label: string;
  bridges: Bridge[];
  totalLength: number;
  condition: ConditionSummary;
  compliance: ComplianceSummary;
  screening: Record<ScreeningBand, number>;
  consequence: ConsequenceCoverage;
}

/** What an unplaceable group is called on screen. Not a group name — an absence. */
export const UNGROUPED_LABEL = 'Not recorded';

/**
 * The register broken into groups, each rolled up.
 *
 * Ordered by size, with the unattributed group last however big it is: it is
 * not a division, and sorting it to the top of a division report would put an
 * absence where a reader expects the biggest office.
 */
export function groupSummaries(
  list: readonly Bridge[],
  by: GroupBy,
  now: Date = new Date(),
): GroupSummary[] {
  const groups = new Map<string | null, Bridge[]>();
  for (const b of list) {
    const key = groupKeyOf(b, by);
    const bucket = groups.get(key);
    if (bucket) bucket.push(b);
    else groups.set(key, [b]);
  }

  return Array.from(groups, ([key, bridges]) => ({
    key,
    label: key ?? UNGROUPED_LABEL,
    bridges,
    totalLength: sumRecorded(bridges, (b) => b.length).total,
    condition: conditionSummary(bridges),
    compliance: complianceSummary(bridges, now),
    screening: bandCounts(bridges),
    consequence: consequenceCoverage(bridges, now),
  })).sort((a, b) => {
    // The absence is always last, whatever its size.
    if ((a.key === null) !== (b.key === null)) return a.key === null ? 1 : -1;
    return b.bridges.length - a.bridges.length || a.label.localeCompare(b.label);
  });
}
